// context/DashboardContext.js
import axios from "axios";
import { useAuth } from "context/AuthContext";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";

const DashboardContext = createContext();

export const useDashboard = () => {
  const ctx = useContext(DashboardContext);
  if (!ctx) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return ctx;
};

export const DashboardProvider = ({ children }) => {
  const { loading: authLoading, token, sessionKey, hasAccess } = useAuth();

  const [dashboardData, setDashboardData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingMerchantDocs, setLoadingMerchantDocs] = useState(false);
  // generic error
  const [error, setError] = useState(null);

  // const [pageSize, setPageSize] = useState(10); // Track the page size (items per page)

  // specific errors
  const [dashboardError, setDashboardError] = useState(null);
  const [transactionsError, setTransactionsError] = useState(null);

  const [retryCount, setRetryCount] = useState(0); // used only by refreshData

  const [allMerchants, setAllMerchants] = useState([]);
  const [merchantDocuments, setMerchantsDocuments] = useState([]);
  const [allMerchantError, setAllMerchantError] = useState(null);
  const [merchantActionError, setMerchantActionError] = useState(null);
  const [merchantDocError, setMechantDocError] = useState(null);
  const [chargeError, setChargeError] = useState(null);

  const hasToken = Boolean(token);
  const clearMerchantActionError = useCallback(() => {
    setMerchantActionError(null);
  }, []);

  // ---------- RESET ----------
  const resetDashboardState = () => {
    setDashboardData(null);
    setTransactions([]);
    setAllMerchants([]);
    setLedgerData([]);
    setLedgerError(null);
    setLedgerLoading(false);
    setMerchantsDocuments([]);
    setError(null);
    setDashboardError(null);
    setTransactionsError(null);
    setAllMerchantError(null);
    setMerchantActionError(null);
    setRetryCount(0);
  };

  // If auth goes away (logout / token missing), clear dashboard state
  useEffect(() => {
    if (!authLoading && !hasToken) {
      resetDashboardState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, hasToken]);

  useEffect(() => {
    resetDashboardState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  // ---------- Dashboard ----------
  const fetchDashboardData = async () => {
    if (!hasToken) return;

    setLoading(true);
    setDashboardError(null);
    // keep generic error optional
    // setError(null);

    try {
      const res = await axios.get(
        "https://merchantapex.fairsurepay.com/api/reports/analytics",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setDashboardData(res.data.data);
      // console.log("Response from context:", res.data.data)
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      const msg =
        err?.response?.data?.message ||
        err.message ||
        "Error fetching dashboard data";
      setDashboardError(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Transactions ----------
  const fetchTransactions = async (pageNo, pageSize) => {
    if (!hasToken) return;

    setLoading(true);
    setLoadingTransactions(true);
    setTransactionsError(null);

    try {
      const res = await axios.get(
        `https://merchantapex.fairsurepay.com/api/reports/transactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // console.log("Response from context:", res);

      // console.log("Transactions from context:", res.data.data?.content);

      setTransactions(res.data.data?.content || []);
      setFilteredTransactions(res.data.data?.content || []);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      const msg = "Error fetching transactions";
      setTransactionsError(msg);
      setError(msg);
    } finally {
      setLoading(false);
      setLoadingTransactions(false);
    }
  };

  // Merchant List
  const fetchAllMerchant = async () => {
    if (!hasToken) return;

    setLoading(true);
    setAllMerchantError(null);

    try {
      const res = await axios.get(
        `https://merchantapex.fairsurepay.com/api/admin/merchant/merchant/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // console.log("Response from context:", res);

      // console.log("AllMerchant from context:", res.data.data);

      setAllMerchants(res.data.data.content || res.data.data || []);
    } catch (err) {
      console.error("Error fetching all merchants:", err);
      const msg = "Error fetching all merchants";
      setAllMerchantError(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Merchant's Upload
  const fetchAllMerchantDocuments = async (merId) => {
    if (!hasToken) return;

    setLoadingMerchantDocs(true);
    setMechantDocError(null);

    try {
      const res = await axios.get(
        `https://merchantapex.fairsurepay.com/api/merchant/all-uploads?merId=${merId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // console.log("Response from context:", res);

      // console.log("Merchant Docs from context:", res.data.data);

      setMerchantsDocuments(res.data.data || []);
    } catch (err) {
      console.error("Error fetching Docs:", err);
      const msg = "Error fetching Docs";
      setMechantDocError(msg);
      setError(msg);
    } finally {
      setLoadingMerchantDocs(false);
      setAllMerchantError(null);
    }
  };

  const executeMerchantAction = async (action, payload) => {
    if (!hasToken || !token) return;

    const actionDetails = {
      confirm: {
        endpoint:
          "https://merchantapex.fairsurepay.com/api/admin/merchant/merchant/confirm",
        error: "Error confirming merchant documents",
        success: "Merchant documents confirmed",
        log: "confirming merchant documents",
      },
      approve: {
        endpoint:
          "https://merchantapex.fairsurepay.com/api/admin/merchant/merchant/approve",
        error: "Error approving merchant",
        success: "Merchant approved successfully",
        log: "approving merchant",
      },
      reject: {
        endpoint:
          "https://merchantapex.fairsurepay.com/api/admin/merchant/merchant/reject-file",
        error: "Error rejecting merchant documents",
        success: "Merchant document rejected",
        log: "rejecting merchant document",
      },
    }[action];

    if (!actionDetails) {
      throw new Error(`Unsupported merchant action: ${action}`);
    }

    setLoading(true);
    setMerchantActionError(null);

    try {
      await axios.post(actionDetails.endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (action === "reject") setMerchantsDocuments([]);
      toast.success(actionDetails.success);
    } catch (err) {
      console.error(`Error ${actionDetails.log}:`, err);
      setMerchantActionError(actionDetails.error);
      toast.error(actionDetails.error);
    } finally {
      setLoading(false);
    }
  };

  const confirmMerchant = ({ merchantId }) =>
    executeMerchantAction("confirm", { merchantId });

  const approveMerchant = ({ merchantId }) =>
    executeMerchantAction("approve", { merchantId });

  const rejectDocuments = (payload) => executeMerchantAction("reject", payload);

  const confirmDocument = async ({ merchantId, fileId }) => {
    if (!hasToken || !token) return;

    setLoading(true);
    setAllMerchantError(null);

    try {
      await axios.post(
        "https://merchantapex.fairsurepay.com/api/admin/merchant/merchant/comfirm-file",
        { merchantId, fileId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Optimistic UI update
      setMerchantsDocuments((prev) =>
        prev.map((doc) =>
          doc.id === fileId ? { ...doc, verificationStatus: true } : doc,
        ),
      );
    } catch (err) {
      console.error("Error confirming document:", err);
      setAllMerchantError("Unauthorized or permission denied");
    } finally {
      setLoading(false);
    }
  };

  const setMerchantCharges = async (payload) => {
    if (!hasToken || !token) return;

    setLoading(true);
    setChargeError(null);

    try {
      // Call to the API to set the merchant charges
      await axios.post(
        "https://merchantapex.fairsurepay.com/api/admin/merchant/merchant/setprice",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Charges Set Succesfully");

      // Assuming that the response contains the updated data, apply the changes optimistically
      setAllMerchants((prev) =>
        prev.map((row) =>
          row.merchant.id === payload.id
            ? {
                ...row,
                fee: payload.fee,
                feeBearer: payload.feeBearer, // Update feeBearer if it's part of the payload
                cardFee: payload.cardFee, // If cardFee is updated, reflect this change
                cardFeeBearer: payload.cardFeeBearer, // If cardFeeBearer is updated, reflect this change
                maxFeeAmount: payload.maxFeeAmount, // Update maxFeeAmount if needed
                chargeCard: payload.chargeCard, // Update chargeCard status if needed
              }
            : row,
        ),
      );
    } catch (err) {
      // Log and set more detailed error information
      console.error("Error setting merchant charges:", err);
      setChargeError(err.response?.data?.message || "Error setting charges");
      toast.error("Error setting charges");
    } finally {
      setLoading(false);
      setChargeError(null);
    }
  };

  const getLedger = async () => {
    if (!hasToken || !token) return;
    setLedgerLoading(true);
    setLedgerError(null);

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await axios.get(
        `https://merchantapex.fairsurepay.com/api/admin/blnk/balances`,
        config,
      );

      // console.log("Ledger Data =>", response.data);
      setLedgerData(Array.isArray(response.data) ? response.data : response.data?.data || []);
    } catch (error) {
      console.log("Error Fetching Ledger", error);
      setLedgerError("Unable to load ledger balances. Please try again.");
    } finally {
      setLedgerLoading(false);
    }
  };

  // ---------- INITIAL FETCH ----------
  useEffect(() => {
    if (authLoading) return;
    if (!hasToken) return;

    fetchDashboardData();
    if (hasAccess("transactions")) fetchTransactions();
    if (hasAccess("merchants")) fetchAllMerchant();
    if (hasAccess("ledger")) getLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, hasToken, sessionKey, token]);

  // ---------- MANUAL RETRY ----------
  const refreshData = () => {
    if (!loading && hasToken) {
      setRetryCount((p) => p + 1);
      fetchDashboardData();
      if (hasAccess("transactions")) fetchTransactions();
      if (hasAccess("merchants")) fetchAllMerchant();
      if (hasAccess("ledger")) getLedger();
    }
  };

  const value = {
    dashboardData,
    transactions,
    ledgerData,
    ledgerLoading,
    ledgerError,

    loading,
    loadingTransactions,
    loadingMerchantDocs,

    error,
    dashboardError,
    transactionsError,
    allMerchantError,
    merchantActionError,
    clearMerchantActionError,
    merchantDocError,
    chargeError,

    refreshData,
    retryCount,

    fetchTransactions,
    resetDashboardState,

    filteredTransactions,
    // totalPages: Math.ceil(transactions.length / pageSize),
    // totalItems: transactions.length,

    // setPageSize,

    allMerchants,
    fetchAllMerchant,
    fetchAllMerchantDocuments,
    merchantDocuments,

    rejectDocuments,
    confirmDocument,
    confirmMerchant,
    approveMerchant,
    setMerchantCharges,

    getLedger,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
